import React from "react";
import { Link } from "react-router-dom";

import {
  Breadcrumbs,
  DemenagementLayout,
  DictimmoCta,
  InteractiveChecklist,
  InternalLinks,
  SourceList,
  itemListJsonLd,
  makeBreadcrumbs,
} from "./DemenagementCommon";

const path = "/checklists/checklist-demenagement-pdf";
const breadcrumbs = makeBreadcrumbs([
  { name: "Déménagement", path: "/demenagement" },
  { name: "Checklist déménagement à imprimer", path },
]);

const sources = [
  {
    label: "Service-public : déménagement",
    href: "https://www.service-public.fr/particuliers/vosdroits/F14128",
  },
  {
    label: "Service-public : changement d'adresse en ligne",
    href: "https://www.service-public.fr/particuliers/vosdroits/R11193",
  },
];

const sections = [
  {
    title: "Avant de donner son préavis",
    items: [
      { id: "dem-verifier-futur-logement", label: "Vérifier le futur logement et son adresse avec etatdulieu.com" },
      { id: "dem-budget", label: "Estimer le budget global : loyer, charges, dépôt, transport, travaux éventuels" },
      { id: "dem-delai-preavis", label: "Vérifier le délai de préavis applicable au logement quitté" },
      { id: "dem-dossier", label: "Rassembler bail, quittances, diagnostics et documents utiles" },
    ],
  },
  {
    title: "3 mois avant",
    items: [
      { id: "dem-envoyer-preavis", label: "Envoyer le préavis par un mode permettant de prouver la date" },
      { id: "dem-organiser-demenagement", label: "Organiser le déménagement : proches, location d'utilitaire ou déménageurs" },
      { id: "dem-devis", label: "Comparer les devis ou réserver le véhicule assez tôt" },
      { id: "dem-tri", label: "Trier, donner, vendre ou recycler ce qui ne partira pas" },
    ],
  },
  {
    title: "1 mois avant",
    items: [
      { id: "dem-assurance", label: "Prévenir ou souscrire l'assurance habitation" },
      { id: "dem-changement-adresse", label: "Faire la déclaration de changement d'adresse sur le site du service public" },
      { id: "dem-energie", label: "Transférer ou ouvrir les contrats énergie" },
      { id: "dem-internet", label: "Anticiper internet et téléphone" },
      { id: "dem-courrier", label: "Mettre en place le suivi du courrier si nécessaire" },
      { id: "dem-banque", label: "Prévenir la banque et les organismes importants" },
      { id: "dem-organismes", label: "Informer CAF, CPAM, impôts ou autres organismes selon votre situation" },
      { id: "dem-ecole", label: "Prévenir école, crèche ou services périscolaires si nécessaire" },
    ],
  },
  {
    title: "1 semaine avant",
    items: [
      { id: "dem-cartons", label: "Étiqueter les cartons par pièce et garder un carton essentiel à part" },
      { id: "dem-clefs", label: "Confirmer les horaires, accès, ascenseur, stationnement et remise des clés" },
      { id: "dem-etat-sortie", label: "Confirmer le rendez-vous d'état des lieux de sortie" },
      { id: "dem-menage", label: "Prévoir le nettoyage du logement quitté" },
    ],
  },
  {
    title: "Jour du déménagement",
    items: [
      { id: "dem-relever-compteurs", label: "Relever les compteurs du logement quitté et du nouveau logement" },
      { id: "dem-photos", label: "Prendre des photos utiles avant départ et à l'arrivée" },
      { id: "dem-etat-entree", label: "Faire l'état des lieux d'entrée avec attention" },
      { id: "dem-boite-outils", label: "Garder papiers, chargeurs, clés, médicaments et outils accessibles" },
    ],
  },
  {
    title: "Après l'emménagement",
    items: [
      { id: "dem-adresse-finale", label: "Finaliser le changement d'adresse auprès des services concernés" },
      { id: "dem-carte-grise", label: "Mettre à jour la carte grise si nécessaire" },
      { id: "dem-contrats", label: "Vérifier la mise en service énergie, eau, internet et assurance" },
      { id: "dem-quartier", label: "Repérer transports, services, médecins, écoles et commerces proches" },
    ],
  },
];

export default function ChecklistDemenagement() {
  const checklistJsonLd = itemListJsonLd({
    name: "Checklist déménagement locataire à imprimer",
    description: "Liste imprimable pour préparer un déménagement : préavis, assurance, énergie, internet, changement d'adresse, compteurs et état des lieux.",
    path,
    items: sections.map((section) => ({
      name: section.title,
      path,
    })),
  });

  return (
    <DemenagementLayout
      title="Checklist déménagement PDF gratuite à imprimer"
      description="Checklist déménagement locataire interactive et imprimable : préavis, assurance, énergie, internet, changement d'adresse, état des lieux et compteurs."
      path={path}
      breadcrumbs={breadcrumbs}
      label="Checklist imprimable"
      printMode="document"
      jsonLd={[checklistJsonLd]}
    >
      <Breadcrumbs items={breadcrumbs} />

      <header className="move-tool-header">
        <h1>Checklist déménagement à imprimer</h1>
        <p>
          Une liste simple à cocher pour préparer le départ, organiser les démarches
          et ne pas oublier les vérifications essentielles du futur logement.
        </p>
        <div className="move-actions no-print">
          <Link className="move-btn move-btn-secondary" to="/outils/lettre-preavis-location">
            Générer une lettre de préavis
          </Link>
        </div>
      </header>

      <section className="move-note no-print" aria-labelledby="checklist-demenagement-seo-title">
        <h2 id="checklist-demenagement-seo-title">Une checklist utile pour locataires et nouveaux occupants</h2>
        <p>
          Cette liste couvre les recherches pratiques les plus fréquentes :
          checklist déménagement locataire, démarches avant changement d'adresse,
          contrats énergie et internet, état des lieux, relevé de compteurs et
          organismes à prévenir.
        </p>
      </section>

      <InteractiveChecklist
        title="Checklist déménagement"
        intro="Cochez les points au fur et à mesure, puis imprimez la checklist si vous voulez la garder avec vos documents."
        sections={sections}
        printEventName="checklist_demenagement_printed"
      />

      <DictimmoCta
        title="Vous connaissez votre future adresse ? Vérifiez-la avant le déménagement."
        text="Dictimmo rassemble les prix du secteur, les risques naturels, l'environnement, la météo, les services, les transports et les données de la commune."
        buttonLabel="Vérifier ma future adresse"
        eventName="dictimmo_cta_clicked_from_checklist_demenagement"
      />

      <InternalLinks currentPath={path} />
      <SourceList sources={sources} />
    </DemenagementLayout>
  );
}
